/**
 * <hy-announcement-bar> (AP-295): cycles through the messages of the
 * announcement bar every `data-interval` milliseconds. Only loaded when the
 * section setting `autoplay` is on and more than one message exists.
 *
 * - Pauses while the pointer is over the bar or focus is inside it, so a
 *   link can be read and reached before it fades away.
 * - Does not cycle at all under prefers-reduced-motion: the first message
 *   stays.
 * - Stops while the tab is hidden, so returning to the tab never shows a
 *   burst of skipped messages.
 * - In the theme editor, selecting a message block shows that message.
 *
 * Without JavaScript the first message (server-side `is-active`) is shown.
 */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const ACTIVE_CLASS = 'is-active';
const MIN_INTERVAL_MS = 3000;

class HyAnnouncementBar extends HTMLElement {
  #timer = 0;
  #messages = [];
  #index = 0;
  #hovered = false;
  #focused = false;

  connectedCallback() {
    this.#messages = [...this.querySelectorAll('[data-hy-announcement]')];
    if (this.#messages.length < 2) return;

    const active = this.#messages.findIndex((message) => message.classList.contains(ACTIVE_CLASS));
    this.#index = Math.max(0, active);

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('shopify:block:select', this.#onBlockSelect);
    document.addEventListener('visibilitychange', this.#sync);
    REDUCED_MOTION.addEventListener('change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.#stop();
    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('visibilitychange', this.#sync);
    REDUCED_MOTION.removeEventListener('change', this.#sync);
  }

  get #interval() {
    const value = Number(this.dataset.interval);
    return Number.isFinite(value) && value > 0 ? Math.max(MIN_INTERVAL_MS, value) : 0;
  }

  #sync = () => {
    const paused = this.#hovered || this.#focused || document.hidden || REDUCED_MOTION.matches;
    if (paused || !this.#interval) this.#stop();
    else this.#start();
  };

  #start() {
    if (this.#timer) return;
    this.#timer = window.setInterval(() => this.#show(this.#index + 1), this.#interval);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = 0;
  }

  #show(index) {
    const next = (index + this.#messages.length) % this.#messages.length;
    this.#messages[this.#index]?.classList.remove(ACTIVE_CLASS);
    this.#messages[next].classList.add(ACTIVE_CLASS);
    this.#index = next;
  }

  #onPointerEnter = () => {
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    this.#sync();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#sync();
  };

  #onFocusOut = (event) => {
    if (this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#sync();
  };

  #onBlockSelect = (event) => {
    const index = this.#messages.indexOf(event.target.closest('[data-hy-announcement]'));
    if (index < 0) return;
    this.#stop();
    this.#show(index);
  };
}

if (!customElements.get('hy-announcement-bar')) {
  customElements.define('hy-announcement-bar', HyAnnouncementBar);
}
